/**
 * Where an authentication provider sends the customer back to.
 *
 * On the web this is `window.location.origin`, and always was. Inside a native shell that value is
 * NOT a web address: Android serves the bundle from `https://localhost` and iOS from
 * `capacitor://localhost`. Handing either to Supabase produces a redirect that is not on any
 * allow-list, that Google will not accept for a production client, and that resolves to nothing a
 * browser can reach. Sign-in simply fails, and it fails after the customer has already typed their
 * password, which is the worst place for it.
 *
 * So a store build returns instead through the app's own URL scheme, `com.nuvrahq.app://...`. That
 * scheme is registered in the Android manifest and in Info.plist, the OS routes it back into the
 * running app, and `nativeShell.ts` completes the session from it. It works without waiting on App
 * Links or Universal Links verification, which matters because those depend on a signing
 * fingerprint that does not exist until the first upload.
 *
 * Every place that hands a provider a return address uses this. There is no second answer.
 *
 * OWNER ACTION, and it cannot be done from here: `com.nuvrahq.app://*` must be added to the
 * Supabase Auth redirect allow-list (Authentication, URL Configuration, Redirect URLs). Supabase
 * refuses any redirect that is not listed, so until it is, native sign-in returns an error rather
 * than a session.
 */

import { isNative } from "./platform";

/**
 * The scheme both native projects register. It is the applicationId / bundle id, which is what
 * keeps it globally unique: no other app can claim it, so no other app can intercept a sign-in.
 */
export const NATIVE_URL_SCHEME = "com.nuvrahq.app";

/**
 * Build a return address for an auth provider.
 *
 * `path` is the route the customer should land on, exactly as it is written on the web, so callers
 * do not have to think about the surface at all.
 */
export function authRedirectUrl(path: string): string {
  const normalised = path.startsWith("/") ? path : `/${path}`;

  if (isNative()) {
    // A custom scheme has no meaningful host, so the route becomes the host plus path
    // ("com.nuvrahq.app://auth/callback"). Both the Android intent filter and the iOS
    // CFBundleURLSchemes entry match on scheme alone, so any route works without further
    // registration.
    return `${NATIVE_URL_SCHEME}:/${normalised}`;
  }

  // Unchanged for the web, deliberately: this must not alter a single thing about how a browser
  // signs in today.
  return `${typeof window === "undefined" ? "" : window.location.origin}${normalised}`;
}

/**
 * True when a URL handed to the app is one of our own auth returns.
 *
 * Used by the native shell to decide whether an incoming deep link is a sign-in completion or an
 * ordinary link. Deliberately narrow: an unrecognised link is left to normal routing rather than
 * being fed to the auth client.
 */
export function isAuthReturnUrl(url: string): boolean {
  if (!url.startsWith(`${NATIVE_URL_SCHEME}:`)) return false;
  return /\/auth\/|\/reset-password|[?#].*(?:code=|access_token=|error=)/.test(url);
}
